/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-hooks/exhaustive-deps */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import { FaroFeatures, RadpairReportAction } from "../../constant";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { BlockUI } from "../BlockUI";
import { LogLevel } from "@grafana/faro-web-sdk";
import { useFlag } from "@unleash/proxy-client-react";
import faroLogger from "../../utils/faro";

// =========================== TYPES ==========================
const OPEN_REPORT_TIMEOUT = 3000;
const MAX_RETRIES_REPORT_OPEN = 1;
interface RadPairEditorProps {
  url: string;
  isDark?: boolean;
  loading?: boolean;
  onReportOpened?: (reportId: string) => void;
  onAppReady?: () => void;
  onAppAuthenticated?: () => void;
  onReportUpdated?: () => void;
}

const IFRAME_PARENT_EVENT_TYPES = {
  SIGN_IN_SUCCESSFUL: "sign_in_successful",
  SIGN_OUT: "sign_out",
  APP_READY: "app_ready",
  REPORT_OPENED: "report_opened",
  REPORT_DOT_OPENED: "report.opened",
  REPORT_CLOSED: "report_closed",
  REPORT_UPDATED: "report.updated",
};

const RADPAIR_DARK_THEME = {
  theme: {
    background: {
      dark: "#232331",
      default: "#232331",
      medium: "#232331",
      // extraDark: "#252B36",
    },
    secondary: {
      main: "#3B967D", // Some input box strokes/borders
    },
    primary: {
      main: "#232331", // Primary brand color
      light: "#6A39CB", // Highlights
    },
  },
};

export type RadPairEditorRef = {
  getIframe: () => HTMLIFrameElement | null;
  postMessage: (data: any) => void;
  handleOpenReport: (reportId: string) => void;
  handleDiscardAndCloseReport: (reportId: string, refresh?: boolean) => void;
  handleAuthentication: (accessToken: string) => void;
  handleSignReport: () => void;
};

type RadpairFaroLogParams = {
  message: string;
  level?: string;
  arg?: any;
};

export const RadPairEditor = forwardRef<RadPairEditorRef, RadPairEditorProps>(
  (
    {
      url,
      onAppReady,
      onAppAuthenticated,
      onReportOpened,
      loading,
      onReportUpdated,
    },
    ref
  ) => {
    const reportOpenTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const reportRetryRef = useRef(0);
    // Radpair echoes the report's existing content once via REPORT_UPDATED right after it loads;
    // a ref (not state) survives across that event without a re-render, letting us compare each
    // new REPORT_UPDATED length against the last known one instead of the misleading first echo.
    const contentLengthRef = useRef(0);
    const enabledRadpairRetryOpenReport = useFlag(
      "enabled-radpair-retry-open-report"
    );
    // =========================== STATES ==========================
    const [iframeKey, setIframeKey] = useState(0);
    const radpairRef = useRef<HTMLIFrameElement>(null);
    const [iframeEventAvailable, setIframeEventAvailable] = useState(false);
    // =========================== LOGGER ==========================
    const logger = ({
      message,
      level = LogLevel.INFO,
    }: RadpairFaroLogParams) => {
      switch (level) {
        case LogLevel.INFO:
          faroLogger.info(message, {
            feature: FaroFeatures.RADPAIR,
          });
          break;
        case LogLevel.WARN:
          const msgWar = message;
          faroLogger.warn(msgWar, {
            feature: FaroFeatures.RADPAIR,
          });
          break;
        default:
          break;
      }
    };

    // =========================== FUNCTIONS ==========================

    const onPostMessage = (data: any) => {
      try {
        if (!radpairRef?.current || !radpairRef.current?.contentWindow) {
          console.error("[onPostMessage] Iframe is crash, Can't post message.");
          logger({
            message: "[onPostMessage] Iframe is crash, Can't post message.",
          });
          return;
        }
        const action = Object.keys(data)[0];
        const value = Object.values(data)[0];
        logger({
          message: "[onPostMessage] Post Message Action: " + action,
        });
        logger({
          message:
            "[onPostMessage] Post Message Value: " + JSON.stringify(value),
        });
        radpairRef.current.contentWindow.postMessage(data, url);
        return;
      } catch (error) {
        logger({
          message: "[onPostMessage] Error posting message to iframe: " + error,
          level: LogLevel.ERROR,
        });
      }
    };

    const clearReportOpenTimeout = () => {
      if (reportOpenTimeoutRef.current) {
        clearTimeout(reportOpenTimeoutRef.current);
        reportOpenTimeoutRef.current = null;
      }
      reportRetryRef.current = 0;
    };

    const startReportOpenTimeout = (reportOpenId: string) => {
      clearReportOpenTimeout();

      reportOpenTimeoutRef.current = setTimeout(() => {
        if (reportRetryRef.current < MAX_RETRIES_REPORT_OPEN) {
          reportRetryRef.current++;

          logger({
            message: `Timeout waiting for REPORT_OPENED. Retrying... Report ID: ${reportOpenId}`,
            level: LogLevel.INFO,
          });

          onPostMessage({ report_id: reportOpenId });

          if (enabledRadpairRetryOpenReport) {
            // Start timeout again for the retry
            startReportOpenTimeout(reportOpenId);
          }

          return;
        }

        logger({
          message: `Failed to receive REPORT_OPENED after retry. Report ID: ${reportOpenId}`,
        });

        clearReportOpenTimeout();
      }, OPEN_REPORT_TIMEOUT);
    };

    const handleAuthentication = useCallback((accessToken: string) => {
      onPostMessage({
        authentication: `Bearer ${accessToken}`,
      });
    }, []);

    const refreshIframe = () => {
      setIframeKey((prev) => prev + 1);
    };

    const handleDiscardAndCloseReport = useCallback(
      (reportId: string, refresh = false) => {
        if (refresh) {
          refreshIframe();
        } else {
          onPostMessage({
            report_action: RadpairReportAction.DISCARD_AND_CLOSE_REPORT,
          });
        }
      },
      []
    );

    const handleSignReport = useCallback(() => {
      onPostMessage({ report_action: RadpairReportAction.SIGN_REPORT });
    }, []);

    const handleOpenReport = useCallback((reportOpenId: string) => {
      if (!reportOpenId) {
        logger({
          message: "Can't post message to iframe caused report id is empty",
        });
        return;
      }
      // The iframe/component never unmounts between reports, so contentLengthRef would still hold
      // the previous report's length; reset it here so THIS report gets its own fresh baseline.
      contentLengthRef.current = 0;
      reportRetryRef.current = 0;
      onPostMessage({ report_id: reportOpenId }); // retry one time
      onPostMessage({
        hide_multi_author_actions: {
          signButton: true,
          actionsButton: true,
          addAddendumButton: true,
        },
      });
      startReportOpenTimeout(reportOpenId);
    }, []);

    const receiveMessage = (message: MessageEvent) => {
      const dataReceived = message?.data;
      const messageEvent = dataReceived?.event;

      if (!messageEvent || message?.origin !== url) return;
      setIframeEventAvailable(true);

      switch (messageEvent) {
        case IFRAME_PARENT_EVENT_TYPES.APP_READY:
          logger({
            message: "[ReceiveMessage] Iframe ready to post/receive message",
          });
          if (onAppReady) {
            onAppReady();
          }
          break;

        case IFRAME_PARENT_EVENT_TYPES.SIGN_IN_SUCCESSFUL:
          if (onAppAuthenticated) {
            onAppAuthenticated();
          }
          logger({
            message: "[ReceiveMessage] Authenticated",
          });
          break;
        case IFRAME_PARENT_EVENT_TYPES.REPORT_UPDATED: {
          const newContentLength =
            dataReceived?.payload?.html_final_report?.length;
          // First REPORT_UPDATED after opening a report is just Radpair echoing existing
          // content, not a user edit — capture it as the baseline and ignore it.
          if (
            contentLengthRef.current === 0 ||
            contentLengthRef.current === newContentLength
          ) {
            if (contentLengthRef.current === 0)
              contentLengthRef.current = newContentLength;
            return;
          }
          // Length actually differs from the baseline -> a real edit happened.
          contentLengthRef.current = newContentLength;
          onReportUpdated?.();
          break;
        }
        case IFRAME_PARENT_EVENT_TYPES.REPORT_OPENED:
        case IFRAME_PARENT_EVENT_TYPES.REPORT_DOT_OPENED:
          if (enabledRadpairRetryOpenReport) clearReportOpenTimeout();

          const eventReportId = dataReceived?.payload
            ? dataReceived?.payload.report_id
            : null;
          if (onReportOpened) onReportOpened(eventReportId);
          logger({
            message: "[ReceiveMessage] Open Report: " + eventReportId,
          });
          break;
        case IFRAME_PARENT_EVENT_TYPES.REPORT_CLOSED:
          break;
        default:
          break;
      }
    };

    useImperativeHandle(ref, () => ({
      getIframe: () => radpairRef.current,
      postMessage: onPostMessage,
      handleOpenReport: handleOpenReport,
      handleDiscardAndCloseReport: handleDiscardAndCloseReport,
      handleAuthentication: handleAuthentication,
      handleSignReport: handleSignReport,
    }));

    // =========================== EFFECTS ==========================
    useEffect(() => {
      if (iframeEventAvailable) {
        onPostMessage(RADPAIR_DARK_THEME);
        onPostMessage({ headless: true });
        onPostMessage({
          report_action: RadpairReportAction.STOP_RECORDING,
        });
      }
    }, [iframeEventAvailable]);

    //  listener
    useEffect(() => {
      window.addEventListener("message", receiveMessage);
      return () => {
        window.removeEventListener("message", receiveMessage);
      };
    }, []);

    const allowAttr = () => {
      let value = "microphone; fullscreen";
      if (window.isSecureContext) {
        value += "; hid";
      }
      return value;
    };

    return (
      <BlockUI isBlocked={loading}>
        <iframe
          key={iframeKey}
          ref={radpairRef}
          src={url}
          allow={allowAttr()}
          allowFullScreen
          frameBorder="0"
          style={{ width: "100%", height: "100%" }}
          // @ts-ignore
          credentialless="true"
        />
      </BlockUI>
    );
  }
);
